import { FrameworkHierarchy } from "@/contexts/entities/EntitiesContext";
import { Activity, Competency, ApiLearnerOutcome } from "../models";
import { getBreadcrumbsFromId } from "../utils";
import { EventType, handleEvent, handlePageView } from "./base";

export const handleNavigationPageView = (
  learnerOutcomes: ApiLearnerOutcome[],
  selectionQuery: string | null,
) => {
  const eventProperties = getNavigationPageViewProperties(
    learnerOutcomes,
    selectionQuery,
  );
  handlePageView(eventProperties);
};

export const handleSubCompetencyClick = (params: SubCompetencyClickParams) => {
  const eventProperties = getSubcompetencyClickProperties(params);
  handleEvent(EventType.NavigationClick, eventProperties);
};

export const handleShareLinkClick = (entityHierarchy: FrameworkHierarchy) => {
  const eventProperties = getShareLinkClickProperties(entityHierarchy);
  handleEvent(EventType.ShareClick, eventProperties);
};

export const handleExternalResourceLinkClick = (
  params: ExternalResourceLinkClickParams,
) => {
  const eventProperties = getExternalResourceLinkClickProperties(params);
  handleEvent(EventType.OutboundClick, eventProperties);
};

export const handleActivitySlidesButtonsClick = (
  params: ActivityButtonsClickParams,
) => {
  const eventProperties = getActivitySlidesButtonsClickProperties(params);
  handleEvent(EventType.OutboundClick, eventProperties);
};

export const handleOutboundClick = (url: string) => {
  handleEvent(EventType.OutboundClick, { target: "footer", url });
};

export const handleInternalNavigationButtonClick = (
  params: InternalNavigationButtonClickParams,
) => {
  const eventProperties = getInternalNavigationButtonClickProperties(params);
  handleEvent(EventType.NavigationClick, eventProperties);
};

export const handleBreadcrumbNavigationClick = (
  params: BreadcrumbNavigationClickParams,
) => {
  const eventProperties = getBreadcrumbNavigationClickProperties(params);
  handleEvent(EventType.NavigationClick, eventProperties);
};

export const handleMapListViewToggleButtonClick = (
  params: getMapListViewToggleButtonClickParams,
) => {
  const eventProperties = getToggleMapListViewButtonClickProperties(params);
  handleEvent(EventType.ToggleViewClick, eventProperties);
};

export const handleBackToNavigatorClick = () => {
  handleEvent(EventType.NavigationClick, { target: "return_to_navigator" });
};

export const handleActivityCardClick = (params: ActivityCardClickParams) => {
  const eventProperties = getActivityCardClickProperties(params);
  handleEvent(EventType.NavigationClick, eventProperties);
};

export const handleActivityTileClick = (params: ActivityTileClickParams) => {
  const eventProperties = getActivityTileClickProperties(params);
  handleEvent(EventType.NavigationClick, eventProperties);
};

export const handleCompetencyCardClick = (
  params: CompetencyCardClickParams,
) => {
  const eventProperties = getCompetencyCardClickProperties(params);
  handleEvent(EventType.NavigationClick, eventProperties);
};

export const handleNavLinkClick = (param: string) => {
  handleEvent(EventType.NavigationClick, {
    target: "clicked_from_nav_bar",
    param,
  });
};

export const handleAccountNavLinkClick = ({
  target,
  param,
}: AccountNavParams) => {
  handleEvent(EventType.NavigationClick, {
    target: "clicked_from_account_nav_" + target,
    param,
  });
};

export const handleDrawerOpen = (entityHierarchy: FrameworkHierarchy) => {
  const eventProperties = getDrawerEventProperties(entityHierarchy);
  handleEvent(EventType.OpenDrawer, eventProperties);
};

export const handleDrawerClose = (entityHierarchy: FrameworkHierarchy) => {
  const eventProperties = getDrawerEventProperties(entityHierarchy);
  handleEvent(EventType.CloseDrawer, eventProperties);
};

export const handleDrawerModalOpen = (entityHierarchy: FrameworkHierarchy) => {
  const eventProperties = getDrawerModalEventProperties(entityHierarchy);
  handleEvent(EventType.OpenModal, eventProperties);
};

export const handleDrawerModalClose = (entityHierarchy: FrameworkHierarchy) => {
  const eventProperties = getDrawerModalEventProperties(entityHierarchy);
  handleEvent(EventType.CloseModal, eventProperties);
};

export const getNavigationPageViewProperties = (
  learnerOutcomes: ApiLearnerOutcome[],
  query: string | null,
) => {
  if (!query) {
    return {};
  }

  const breadcrumbs = getBreadcrumbsFromId(learnerOutcomes, query);
  const hierarchy_level = breadcrumbs.competency
    ? "competency"
    : "learner_outcome";

  return {
    hierarchy_level,
    competency: breadcrumbs.competency?.name,
    learner_outcome: breadcrumbs.learnerOutcome?.name,
  };
};

type ExternalResourceLinkClickParams = {
  entityHierarchy: FrameworkHierarchy;
  url: string;
};

export const getExternalResourceLinkClickProperties = ({
  entityHierarchy,
  url,
}: ExternalResourceLinkClickParams) => {
  return {
    url,
    target: entityHierarchy.subCompetency ? "subcompetency" : "competency",
    subcompetency: entityHierarchy.subCompetency?.name,
    competency: entityHierarchy.competency?.name,
    learner_outcome: entityHierarchy.learnerOutcome?.name,
  };
};

type InternalNavigationButtonClickParams = {
  buttonName: string;
  competency?: string;
  learnerOutcome?: string;
  navigatingToCompetencyName?: string;
};

export const getInternalNavigationButtonClickProperties = ({
  buttonName,
  competency,
  learnerOutcome,
  navigatingToCompetencyName,
}: InternalNavigationButtonClickParams) => {
  return {
    target: "internal_navigation_button",
    button_name: buttonName,
    learner_outcome: learnerOutcome,
    competency: competency,
    navigating_to_competency: navigatingToCompetencyName,
  };
};

type BreadcrumbNavigationClickParams = {
  buttonName: string;
  navigatingTo?: string;
};

export const getBreadcrumbNavigationClickProperties = ({
  buttonName,
  navigatingTo,
}: BreadcrumbNavigationClickParams) => {
  return {
    target: "breadcrumb_navigation_link",
    button_name: buttonName,
    navigating_to: navigatingTo,
  };
};

type getMapListViewToggleButtonClickParams = {
  buttonName: string;
  action: string;
};

export const getToggleMapListViewButtonClickProperties = ({
  buttonName,
  action,
}: getMapListViewToggleButtonClickParams) => {
  return {
    target: "map_list_view_toggle_button",
    button_name: buttonName,
    action: action,
  };
};

type ActivityButtonsClickParams = {
  buttonName: string;
  activityName: string;
  activitySlug: string;
  hierarchy: FrameworkHierarchy;
};

export const getActivitySlidesButtonsClickProperties = ({
  buttonName,
  activityName,
  activitySlug,
  hierarchy,
}: ActivityButtonsClickParams) => {
  return {
    button_name: buttonName,
    activity_title: activityName,
    activity_slug: activitySlug,
    competency: hierarchy.competency?.name,
    learner_outcome: hierarchy.learnerOutcome?.name,
  };
};

export enum ClickActions {
  Expand = "expand",
  Collapse = "collapse",
}

export type SubCompetencyClickParams = {
  entityHierarchy: FrameworkHierarchy;
  action: ClickActions;
};

export const getSubcompetencyClickProperties = ({
  entityHierarchy,
  action,
}: SubCompetencyClickParams) => {
  return {
    target: "subcompetency",
    subcompetency: entityHierarchy.subCompetency?.name || "all",
    competency: entityHierarchy.competency?.name,
    learner_outcome: entityHierarchy.learnerOutcome?.name,
    action,
  };
};

export const getShareLinkClickProperties = (
  entityHierarchy: FrameworkHierarchy,
) => {
  return {
    target: entityHierarchy.subCompetency ? "subcompetency" : "competency",
    type: "link",
    subcompetency: entityHierarchy.subCompetency?.name,
    competency: entityHierarchy.competency?.name,
    learner_outcome: entityHierarchy.learnerOutcome?.name,
  };
};

type ActivityCardClickParams = {
  activity: Activity;
  carouselIndex?: number;
};

export const getActivityCardClickProperties = ({
  activity,
  carouselIndex,
}: ActivityCardClickParams) => {
  return {
    activity_title: activity.name,
    activity_url_slug: activity.urlSlug,
    competency: activity.competencyName,
    subcompetency: activity.subCompetencyName,
    carousel_rank: carouselIndex,
    target: "activity",
  };
};

type ActivityTileClickParams = {
  activity: Activity;
};

export const getActivityTileClickProperties = ({
  activity,
}: ActivityTileClickParams) => {
  return {
    activity_title: activity.name,
    component_skill_name: activity.subCompetencyName,
    competency_name: activity.competencyName,
    learner_outcome_id: activity.learnerOutcomeId,
    target: "activity tile",
  };
};

type CompetencyCardClickParams = {
  competency: Competency;
  activityCount?: number;
};

export const getCompetencyCardClickProperties = ({
  competency,
  activityCount,
}: CompetencyCardClickParams) => {
  return {
    competency_name: competency.name,
    activity_count: activityCount,
  };
};

export const getDrawerEventProperties = (
  entityHierarchy: FrameworkHierarchy,
) => {
  return {
    competency: entityHierarchy.competency?.name,
    learner_outcome: entityHierarchy.learnerOutcome?.name,
  };
};

export const getDrawerModalEventProperties = (
  entityHierarchy: FrameworkHierarchy,
) => {
  return {
    component_skill: entityHierarchy.subCompetency?.name,
    competency: entityHierarchy.competency?.name,
    learner_outcome: entityHierarchy.learnerOutcome?.name,
  };
};

type AccountNavParams = {
  target: string;
  param: string;
};
